import React, { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/styles';
import { Button } from 'design-system/Button';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Select, Option } from 'react-scale/components/design-system/Select';
import InputSelect from 'corp/components/UI/Select';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';

enum ESCIRelevance {
  ESCI = 'ESCI',
  Relevance = 'Relevance',
}

interface IJobSummary {
  id: string;
  taskCount: number;
}

export function GroupVendorTasks() {
  const [project, setProject] = useState<ESCIRelevance>(ESCIRelevance.ESCI);
  const [jobs, setJobs] = useState<IJobSummary[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [taskCount, setTaskCount] = useState<number>(-1);
  const [isLoading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const classes = useStyles();

  useEffect(() => {
    handleJobsFetch(project);
  }, [project]);

  function handleJobsFetch(project: ESCIRelevance) {
    apiFetch(null, `corp-api/esci-utils/project/${project}/jobs`, {
      method: 'GET',
    })
      .then(result => {
        setJobs(result.jobs);
        setLoading(false);
        return;
      })
      .catch(error => {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setLoading(false);
      });
  }

  function onChangeProject(event: React.ChangeEvent<{ value: ESCIRelevance }>) {
    const newProject = event.target?.value;
    setLoading(true);
    setProject(newProject);
    setMessage(null);
    setJobId(null);
    setTaskCount(-1);

    handleJobsFetch(newProject);
  }

  async function handleSelectJob(event: { value: string }) {
    const jobId = event.value;
    setJobId(jobId);
    setTaskCount(jobs.find(job => job.id === jobId)?.taskCount ?? -1);
    setMessage(null);
  }

  async function handleSubmit() {
    if (!jobId) {
      setMessage('Please select a job from which to group vendor tasks');
      return;
    }
    try {
      const jobIdCopy = jobId;

      setJobId(null);
      setTaskCount(-1);

      const { message }: { message: string } = await apiFetch(
        null,
        'corp-api/esci-utils/group-vendor-tasks',
        {
          method: 'POST',
          body: {
            jobId: jobIdCopy,
            taskType: project,
          },
        },
      );

      setMessage(message);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  }

  return (
    <div className={classes.groupVendorTasks}>
      <div className={`${classes.groupVendorTasks}__selectors`}>
        <div className={`${classes.groupVendorTasks}__projectSelect`}>
          <Select value={project} onChange={onChangeProject}>
            <Option key={ESCIRelevance.ESCI} value={ESCIRelevance.ESCI}>
              {ESCIRelevance.ESCI}
            </Option>
            <Option key={ESCIRelevance.Relevance} value={ESCIRelevance.Relevance}>
              {ESCIRelevance.Relevance}
            </Option>
          </Select>
        </div>
        <div className={`${classes.groupVendorTasks}__jobSelectContainer`}>
          <InputSelect
            name="group-vendor-task-job-selector"
            className={`${classes.groupVendorTasks}__jobSelect`}
            placeholder={isLoading ? 'Loading...' : 'Select Job...'}
            value={jobId}
            disabled={isLoading}
            onChange={handleSelectJob}
            options={jobs.map(job => ({
              label: `${job.id} - ${job.taskCount.toLocaleString()} tasks`,
              value: job.id,
            }))}
          />
          {jobId && (
            <span className={`${classes.groupVendorTasks}__jobMessage`}>
              Found <strong>{taskCount.toLocaleString()}</strong> tasks for job{' '}
              <strong>{jobId}</strong>
            </span>
          )}
        </div>
      </div>
      <div className={`${classes.groupVendorTasks}__submitContainer`}>
        <Button onClick={handleSubmit} disabled={!jobId || taskCount <= 0}>
          Group vendor tasks
        </Button>
      </div>
      {message && <div>{<SanitizedMarkdown text={message} />}</div>}
    </div>
  );
}

const useStyles = makeStyles({
  groupVendorTasks: {
    display: 'flex',
    flexDirection: 'column',
    '&__selectors': {
      display: 'flex',
      flexDirecton: 'row',
    },
    '&__projectSelect': {
      flex: '0 0 5%',
      marginRight: '1rem',
    },
    '&__jobSelectContainer': {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
    },
    '&__jobSelect': {
      flex: '1 1 auto',
      width: '100%',
    },
    '&__jobMessage': {
      marginTop: '10px',
      flex: '0 0 5%',
    },
    '&__submitContainer': {
      margin: '1rem auto',
    },
  },
});
